import { format } from 'date-fns'
import { Order, OrderStatusType, ServiceType } from 'types/order.types'
import { formatDateTimeRangeString } from './date.utils'
import { AddressDestinationType } from 'types/address.types'

type StatusWithTimestamp = {
    status: OrderStatusType
    timestamp?: string | null
}

/**
 * Retrieves the most recent status and its associated timestamp from an order.
 *
 * It accepts an `Order` object and extracts various timestamps (e.g., `quotingAt`, `procuringAt`, etc.)
 * paired with their respective statuses. It filters out any entries
 * without a timestamp and sorts the remaining ones to find the most recent timestamp and status.
 *
 * If no timestamps are present or if the order is undefined, it defaults to the 'quoting' status.
 *
 * @param {Order} order - The order object containing status timestamps.
 * @returns {{ status: OrderStatus; timestamp?: string }} - The most recent status and its timestamp.
 */
export const getLastOrderStatusWithTimestamp = (
    order?: Order
): { status: OrderStatusType; timestamp?: string } => {
    if (!order) {
        return {
            status: 'quoting',
            timestamp: undefined,
        }
    }

    const {
        quotingAt,
        procuringAt,
        bookedAt,
        pickedUpAt,
        deliveredAt,
        cancelledAt,
        onHoldAt,
        quoteLostAt,
    } = order

    // Collect all relevant timestamps with their corresponding status
    const timestamps: {
        timestamp: string | null | undefined
        status: OrderStatusType
    }[] = [
        { timestamp: quotingAt, status: 'quoting' },
        { timestamp: procuringAt, status: 'procuring' },
        { timestamp: bookedAt, status: 'scheduled' },
        { timestamp: cancelledAt, status: 'cancelled' },
        { timestamp: onHoldAt, status: 'onHold' },
        { timestamp: quoteLostAt, status: 'quoteLost' },
        { timestamp: pickedUpAt, status: 'inTransit' },
        { timestamp: deliveredAt, status: 'delivered' },
    ]

    // Find the most recent timestamp and corresponding status
    const mostRecent: StatusWithTimestamp | undefined = timestamps
        .filter((entry) => entry.timestamp) // Filter out entries with no timestamp
        .sort(
            (a, b) =>
                new Date(b.timestamp!).getTime() -
                new Date(a.timestamp!).getTime()
        )?.[0]

    return {
        status: mostRecent?.status ?? 'quoting',
        timestamp: mostRecent?.timestamp ?? undefined,
    }
}

export const getOrderStatus = (order?: Order): OrderStatusType => {
    const { status } = getLastOrderStatusWithTimestamp(order)
    return status
}

export const isOrderCancelledStatus = (order?: Order): boolean => {
    return getOrderStatus(order) === 'cancelled'
}

export const isOrderQuoteLostStatus = (order?: Order): boolean => {
    return getOrderStatus(order) === 'quoteLost'
}

export const getOrderSummary = (order?: Order): string => {
    const { status, timestamp } = getLastOrderStatusWithTimestamp(order)

    if (!status || !timestamp) {
        return '--'
    }

    if (status === 'quoting') {
        return `Providing quote to customer`
    } else if (status === 'procuring') {
        return `Waiting on carrier confirmation`
    } else if (status === 'scheduled') {
        if (!order?.bookedAt) {
            return `Order booked`
        }

        const bookedDate = new Date(order.bookedAt)
        const bookedDateStr = format(bookedDate, 'EEEE MMM do')
        const bookedTimeStr = format(bookedDate, 'h:mm a')
        return `Booked on ${bookedDateStr} at ${bookedTimeStr}`
    } else if (status === 'inTransit') {
        if (!order?.pickedUpAt) {
            return `Order picked up`
        }

        const pickupDate = new Date(order.pickedUpAt)
        const pickupDateStr = format(pickupDate, 'EEEE MMM do')
        const pickupTimeStr = format(pickupDate, 'h:mm a')
        return `Picked up on ${pickupDateStr} at ${pickupTimeStr}`
    } else if (status === 'delivered') {
        if (!order?.deliveredAt) {
            return `Order delivered`
        }

        const deliveryDate = new Date(order.deliveredAt)
        const deliveryDateStr = format(deliveryDate, 'EEEE MMM do')
        const deliveryTimeStr = format(deliveryDate, 'h:mm a')
        return `Delivered on ${deliveryDateStr} at ${deliveryTimeStr}`
    } else if (status === 'cancelled') {
        return `Order cancelled`
    } else if (status === 'onHold') {
        return `Order on hold`
    } else if (status === 'quoteLost') {
        return `Quote lost`
    }

    return '--'
}

/**
 * Generates a mutation object based on the provided order status and optionally an existing order.
 *
 * It takes a `status` and sets relevant timestamps or fields based on the status:
 * - For each status (e.g., 'quoting', 'procuring', 'scheduled', 'inTransit', etc.), it sets the corresponding
 *   timestamp to the current time (`now`) and clears fields that no longer apply.
 *
 * @param {OrderStatusType} status - The new status to set on the order.
 * @returns {Partial<Order>} - The mutation object reflecting the updated order state.
 */

export const getOrderStatusMutation = (
    status: OrderStatusType
): Partial<Order> => {
    const now = new Date().toISOString()

    if (status === 'quoting') {
        return {
            quotingAt: now,
            procuringAt: null,
            bookedAt: null,
            pickedUpAt: null,
            deliveredAt: null,
        }
    } else if (status === 'procuring') {
        return {
            procuringAt: now,
            bookedAt: null,
            pickedUpAt: null,
            deliveredAt: null,
        }
    } else if (status === 'scheduled') {
        return {
            bookedAt: now,
            pickedUpAt: null,
            deliveredAt: null,
        }
    } else if (status === 'inTransit') {
        return {
            pickedUpAt: now,
            deliveredAt: null,
        }
    } else if (status === 'delivered') {
        return { deliveredAt: now }
    } else if (status === 'cancelled') {
        return { cancelledAt: now }
    } else if (status === 'onHold') {
        return { onHoldAt: now }
    } else if (status === 'quoteLost') {
        return { quoteLostAt: now }
    }

    return {}
}

export const getOrderCityState = ({
    order,
    destinationType,
}: {
    order?: Order
    destinationType: AddressDestinationType
}): string => {
    const extraDestinations =
        destinationType === 'pickup'
            ? order?.extraPickups
            : order?.extraDropoffs
    const destination =
        destinationType === 'pickup' ? order?.pickup : order?.dropoff

    if (extraDestinations?.length) {
        return `Multiple`
    }

    return [destination?.city, destination?.state].filter(Boolean).join(', ')
}

export const getOrderExpectedTimestamp = ({
    order,
    destinationType,
}: {
    order?: Order
    destinationType: AddressDestinationType
}): string => {
    const extraDestinations =
        destinationType === 'pickup'
            ? order?.extraPickups
            : order?.extraDropoffs
    const destination =
        destinationType === 'pickup' ? order?.pickup : order?.dropoff

    if (extraDestinations?.length) {
        return `Multiple`
    }

    return formatDateTimeRangeString({
        from: destination?.expectedArrivalStartAt,
        to: !destination?.isAppointmentRequired
            ? destination?.expectedArrivalEndAt
            : undefined,
        hasTime: false,
    })
}

export const isOrderLTL = (order: {
    serviceType?: ServiceType | null
}): boolean => {
    return order?.serviceType === 'ltl'
}
